import { useState, useEffect, useCallback } from 'react';
import styles from './GoogleDrivePicker.module.scss';
import { Button, Card, Spin } from 'antd';
import Toast from '../../helpers/Toast';
import { GoogleOutlined } from '@ant-design/icons';
import PieceJointeService from '../../services/PieceJointeService';
import { catchError } from '../../services/DaoService';
import PropTypes from 'prop-types';

// Clés Google API
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';

const GoogleDrivePicker = ({onUploadFinish,  parentType, parentId}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accessToken, setAccessToken] = useState(localStorage.getItem('googleAccessToken'));
    const [selectedFile, setSelectedFile] = useState(null);
    const [isPickerLoaded, setIsPickerLoaded] = useState(false);
    const [isUploading, setUploading] = useState(false);
    const [isGoogleSdkReady, setIsGoogleSdkReady] = useState(false);

    useEffect(() => {
        // Charger le script GIS
        const scriptGIS = document.createElement('script');
        scriptGIS.src = 'https://accounts.google.com/gsi/client';
        scriptGIS.async = true;
        scriptGIS.defer = true;
        scriptGIS.onload = () => setIsGoogleSdkReady(true);
        scriptGIS.onerror = () => Toast.error("Impossible de charger l'authentification Google.");
        document.body.appendChild(scriptGIS);

        // Charger Google Picker API
        const scriptPicker = document.createElement('script');
        scriptPicker.src = 'https://apis.google.com/js/api.js';
        scriptPicker.async = true;
        scriptPicker.defer = true;
        scriptPicker.onload = () => {
            window.gapi.load('picker', () => setIsPickerLoaded(true));
        };
        scriptPicker.onerror = () => Toast.error("Impossible de charger Google Picker.");
        document.body.appendChild(scriptPicker);

        // Vérifier si un token est déjà stocké localement
        if (accessToken) {
            setIsAuthenticated(true);
        }

        return () => {
            document.body.removeChild(scriptGIS);
            document.body.removeChild(scriptPicker);
        };
    }, [accessToken]);

    useEffect(() => {
        const token = localStorage.getItem('googleAccessToken');
        const expiration = localStorage.getItem('googleTokenExpiration');
    
        if (token && expiration) {
            if (Date.now() > parseInt(expiration, 10)) {
                // Token expiré, suppression et forcer l'authentification
                localStorage.removeItem('googleAccessToken');
                localStorage.removeItem('googleTokenExpiration');
                setAccessToken(null);
                setIsAuthenticated(false);
            } else {
                setAccessToken(token);
                setIsAuthenticated(true);
            }
        } else {
            setAccessToken(null);
            setIsAuthenticated(false);
        }
    }, []);
    

    // Gérer l'authentification
    const handleAuthClick = () => {
        if (!CLIENT_ID) {
            Toast.error("La configuration Google Drive est incomplète.");
            return;
        }

        if (!isGoogleSdkReady || !window.google?.accounts?.oauth2) {
            Toast.error("Google n'est pas encore prêt. Réessaie dans quelques secondes.");
            return;
        }

        setIsLoading(true);

        try {
            window.google.accounts.oauth2.initTokenClient({
                client_id: CLIENT_ID,
                scope: SCOPES,
                callback: (response) => {
                    if (response.access_token) {
                        const expiresIn = response.expires_in || 3600; // 1 heure par défaut
                        const expirationTime = Date.now() + expiresIn * 1000;

                        setAccessToken(response.access_token);
                        setIsAuthenticated(true);

                        localStorage.setItem('googleAccessToken', response.access_token);
                        localStorage.setItem('googleTokenExpiration', expirationTime);

                        Toast.success('Connexion réussie à Google Drive');
                    } else {
                        Toast.error("Échec de l'authentification");
                    }
                    setIsLoading(false);
                },
            }).requestAccessToken();
        } catch (error) {
            console.error('Erreur lors de la connexion à Google Drive:', error);
            Toast.error("Impossible d'ouvrir la connexion Google Drive.");
            setIsLoading(false);
        }
    };    

    // Ouvrir le sélecteur de fichiers Google Drive
    const openPicker = useCallback(() => {
        if (!accessToken) {
            Toast.error('Veuillez vous authentifier avant de sélectionner un fichier');
            return;
        }
        if (!isPickerLoaded) {
            Toast.error("Google Picker API n'est pas encore chargé. Veuillez réessayer.");
            return;
        }

        const picker = new window.google.picker.PickerBuilder()
            .addView(new window.google.picker.DocsView().setIncludeFolders(true))
            .setOAuthToken(accessToken)
            .setDeveloperKey(API_KEY)
            .setCallback(pickerCallback)
            .build();

        picker.setVisible(true);
    }, [accessToken, isPickerLoaded]);

    // Gérer la sélection du fichier
    const pickerCallback = async (data) => {
        if (data.action === window.google.picker.Action.PICKED) {
            const fileId = data.docs[0].id;
            try {
                const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,mimeType,webViewLink,iconLink`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const file = await response.json();
                setSelectedFile(file);
                console.log('Fichier sélectionné:', file);
                setUploading(true);
                PieceJointeService.getInstance().storeFromDrive({
                    nom: file.name,
                    path: file.webViewLink,
                    parent_type: parentType,
                    parent_id: parentId,
                }).then(() => {
                    onUploadFinish();
                    Toast.success('Fichier téléchargé avec succès');
                    setSelectedFile(null);
                })
                .catch(catchError)
                    .finally(() => setUploading(false));                
            } catch (error) {
                console.error('Erreur lors de la récupération du fichier:', error);
                Toast.error('Impossible de récupérer le fichier');
            }
        }
    };

    return (
        <div className={styles.container}>
            <Card className={styles.card}>
                <h2 className={styles.title}>Télécharger un fichier à partir de Google Drive</h2>
                {!isAuthenticated ? <Button onClick={handleAuthClick} disabled={isLoading}>
                    Connexion Google Drive
                </Button>: null}
                {isAuthenticated && (
                    <Button onClick={openPicker} disabled={isLoading || !isPickerLoaded} icon={<GoogleOutlined />}>Choisir un fichier à partir de Google Drive</Button>
                )}
                {selectedFile && (
                    <Spin spinning={isUploading}>
                    <div className={styles.filePreview}>
                        <p><strong>Nom :</strong> {selectedFile.name}</p>
                        <a href={selectedFile.webViewLink} target="_blank" rel="noopener noreferrer">Ouvrir</a>
                    </div>
                    </Spin>
                )}
            </Card>
        </div>
    );
};

GoogleDrivePicker.propTypes = {
    onUploadFinish: PropTypes.func.isRequired,
    parentType: PropTypes.string.isRequired,
    parentId: PropTypes.number.isRequired,
};

export default GoogleDrivePicker;
